import { useMemo } from 'react';

type TimeLimit = {
    h: number;
    m: number;
};

interface DateParts {
    D: number;
    M: number;
    Y: number;
}

export interface TimeConfig {
    interval?: number;
    minTime?: TimeLimit;
    maxTime?: TimeLimit;
    locale?: string;
}

const MINUTES_IN_THE_DAY = 60 * 24;

const getDateParts = (d: Date): DateParts => ({
    D: d.getDate(),
    M: d.getMonth(),
    Y: d.getFullYear(),
});

const newDate = (Y?: number, M?: number, ...rest: number[]): Date =>
    !Y || M == null ? new Date() : new Date(Y, M, ...rest);

const getTimeDate = (
    Y: number,
    M: number,
    D: number,
    t: TimeLimit = { h: 0, m: 0 },
): Date => newDate(Y, M, D, t.h, t.m);

const formatTime = (d: Date, locale?: string) => {
    return d.toLocaleTimeString(locale || [], {
        timeStyle: 'short',
    });
};

const valueTime = (d: Date, locale?: string) => {
    return d.toLocaleTimeString(locale || [], {
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const isBefore = (d1: Date, d2: Date): boolean => d1 < d2;
const isAfter = (d1: Date, d2: Date): boolean => d1 > d2;
const maxDateAndAfter = (maxDate: Date | undefined, date: Date): boolean =>
    !!maxDate && isAfter(date, maxDate);
const minDateAndBefore = (minDate: Date | undefined, date: Date): boolean =>
    !!minDate && isBefore(date, minDate);

const NOW = newDate();
const { Y, M, D } = getDateParts(NOW);

export const useTime = (
    props: TimeConfig,
): {
    time: {
        label: string;
        value: string;
    }[];
} => {
    const { locale, interval = 30, minTime, maxTime } = props;
    const time = useMemo(() => {
        const segments = MINUTES_IN_THE_DAY / interval;
        const maxTimeConfig = maxTime || {
            h: 0,
            m: MINUTES_IN_THE_DAY,
        };
        const minTimeConfig = minTime || {
            h: 0,
            m: 0,
        };
        const t = [];
        const minDate = getTimeDate(Y, M, D, minTimeConfig);
        const maxDate = getTimeDate(Y, M, D, maxTimeConfig);

        for (let i = 0; i < segments; i++) {
            const date = newDate(Y, M, D, 0, i * interval);
            const outsideRange =
                minDateAndBefore(minDate, date) ||
                maxDateAndAfter(maxDate, date);
            if (!outsideRange) {
                t.push({
                    value: valueTime(date, locale),
                    label: formatTime(date, locale),
                });
            }
        }

        return t;
    }, [locale, minTime, maxTime, interval]);

    return {
        time,
    };
};
